import React from 'react';
import { NameEmailAvatar } from '../../../../../../../../../ui';
import { PieCartWithStats } from '../../../../../../../../../ui/components/Common/Charts/PieCartWithStats';
import { BEIResultsData } from '../../AssessmentResultsDetails';
import {
  BEIRadarChart,
  CircularProgressChart,
  MiniProgressRing,
  SkillsBarChart,
} from '../../Components/ModernCharts';
import { Overview } from '../../Components/Overview';

interface BEIAttribute {
  name: string;
  score: number;
  competencyLevel: 'Expert' | 'Proficient' | 'Developing' | 'Basic';
  evidenceCount: number;
}

interface SummaryStats {
  beiAttributes: BEIAttribute[];
  overallCompetencyScore: number;
  situationalResponses: number;
  starFrameworkAdherence: number;
  totalBehavioralExamples: number;
  totalQuestions: number;
  totalFollowUps: number;
  averageScore: number;
  attributesCount: number;
}

interface SummaryProps {
  assessmentData: any;
  beiResults: BEIResultsData[] | null;
  loading: boolean;
  error: string | null;
}

export const Summary: React.FC<SummaryProps> = ({
  assessmentData,
  beiResults,
  loading,
  error,
}) => {
  // Extract candidate information
  const getCandidateInfo = () => {
    // Debug logging to see what data we're getting

    // Extract user information from the API response structure
    const firstName = assessmentData?.user?.firstName || '';
    const lastName = assessmentData?.user?.lastName || '';
    const email = assessmentData?.user?.email || '';
    const fullName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || 'Candidate Name';

    return {
      name: fullName,
      email: email,
      company:
        assessmentData?.assessment?.jobPosition || 'Position Not Specified',
      date: assessmentData?.assessment?.creationDateTime
        ? new Date(
          assessmentData.assessment.creationDateTime * 1000,
        ).toLocaleDateString()
        : assessmentData?.startTime
          ? new Date(assessmentData.startTime * 1000).toLocaleDateString()
          : new Date().toLocaleDateString(),
      avatar: '/api/placeholder/64/64',
    };
  };

  // Calculate comprehensive statistics
  const getComprehensiveStats = () => {
    const stats = {
      overallScore: 0,
      technicalScore: 0,
      behavioralScore: 0,
      totalQuestions: 0,
      correctAnswers: 0,
      timeSpent: 0,
      attributes: 0,
    };

    // BEI Statistics
    if (beiResults && beiResults.length > 0) {
      stats.behavioralScore = Math.round(
        beiResults.reduce((sum, result) => sum + result.score, 0) /
          beiResults.length,
      );
      stats.attributes += beiResults.length;
      stats.totalQuestions += beiResults.reduce(
        (sum, result) => sum + result.questionAnswer.length,
        0,
      );
    }

    // Assessment Statistics
    if (assessmentData?.skills && assessmentData?.questions) {
      const totalCorrect = assessmentData.skills.reduce(
        (sum: number, skill: any) => sum + skill.correctCount,
        0,
      );
      const totalQuestions = assessmentData.skills.reduce(
        (sum: number, skill: any) => sum + skill.totalCount,
        0,
      );
      if (totalQuestions > 0) {
        stats.technicalScore = Math.round(
          (totalCorrect / totalQuestions) * 100,
        );
      }
      stats.correctAnswers += totalCorrect;
      stats.totalQuestions += totalQuestions;
      stats.attributes += assessmentData.skills.length;
      stats.timeSpent = assessmentData.totalTime || 0;
    }

    // Calculate overall score - properly average technical and behavioral scores
    if (stats.technicalScore > 0 && stats.behavioralScore > 0) {
      stats.overallScore = Math.round(
        (stats.technicalScore + stats.behavioralScore) / 2,
      );
    } else if (stats.technicalScore > 0) {
      stats.overallScore = stats.technicalScore;
    } else if (stats.behavioralScore > 0) {
      stats.overallScore = stats.behavioralScore;
    } else if (assessmentData?.behaviorScore) {
      // Use behavioral score from assessment data if available
      stats.behavioralScore = assessmentData.behaviorScore;
      if (stats.technicalScore > 0) {
        stats.overallScore = Math.round(
          (stats.technicalScore + stats.behavioralScore) / 2,
        );
      } else {
        stats.overallScore = stats.behavioralScore;
      }
    }

    return stats;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80)
      return 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-emerald-100';
    if (score >= 60)
      return 'bg-gradient-to-r from-blue-400 to-indigo-500 shadow-blue-100';
    if (score >= 40)
      return 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-amber-100';
    return 'bg-gradient-to-r from-rose-500 to-red-600 shadow-rose-100';
  };

  const getScoreHexColor = (score: number) => {
    if (score >= 80) return '#10B981'; // Emerald-500
    if (score >= 60) return '#3B82F6'; // Blue-500
    if (score >= 40) return '#F59E0B'; // Amber-500
    return '#EF4444'; // Red-500
  };

  const candidateInfo = getCandidateInfo();
  const comprehensiveStats = getComprehensiveStats();
  const hasBeiResults = beiResults && beiResults.length > 0;
  const hasAssessmentData =
    assessmentData &&
    assessmentData.questions &&
    assessmentData.questions.length > 0;
  // Create score cards based on available data - Use same calculation as comprehensive stats
  const summaryStats = React.useMemo<SummaryStats | null>(() => {
    // If no BEI results, return null
    if (!beiResults || beiResults.length === 0) return null;

    // Calculate BEI-specific metrics
    const beiAttributes = beiResults.map((result) => ({
      name: result.attribute,
      score: result.score,
      competencyLevel: (result.score >= 85
        ? 'Expert'
        : result.score >= 70
          ? 'Proficient'
          : result.score >= 55
            ? 'Developing'
            : 'Basic') as 'Expert' | 'Proficient' | 'Developing' | 'Basic',
      evidenceCount: result.questionAnswer.length,
    }));

    // Calculate overall competency score
    const overallCompetencyScore = Math.round(
      beiResults.reduce((sum, result) => sum + result.score, 0) /
        beiResults.length,
    );

    // Calculate STAR framework adherence (example calculation - adjust based on your actual logic)
    const starFrameworkAdherence =
      beiResults.reduce((sum, result) => {
        const hasStarComponents = result.questionAnswer.filter(
          (qa) =>
            qa?.answer &&
            qa.answer.toLowerCase().includes('situation') &&
            qa.answer.toLowerCase().includes('task') &&
            qa.answer.toLowerCase().includes('action') &&
            qa.answer.toLowerCase().includes('result'),
        ).length;
        return sum + (hasStarComponents / result.questionAnswer.length) * 100;
      }, 0) / beiResults.length;

    // Count total behavioral examples
    const totalBehavioralExamples = beiResults.reduce(
      (sum, result) => sum + result.questionAnswer.length,
      0,
    );

    // Count situational responses
    const situationalResponses = beiResults.reduce(
      (sum, result) =>
        sum +
        result.questionAnswer.filter(
          (qa) =>
            qa?.answer &&
            (qa.answer.toLowerCase().includes('situation') ||
              qa.answer.toLowerCase().includes('scenario') ||
              qa.answer.toLowerCase().includes('example')),
        ).length,
      0,
    );

    return {
      beiAttributes,
      overallCompetencyScore,
      situationalResponses,
      starFrameworkAdherence,
      totalBehavioralExamples,
      // Keep original metrics for backward compatibility
      totalQuestions: totalBehavioralExamples,
      totalFollowUps: beiResults.reduce(
        (sum, result) => sum + result.followupCount,
        0,
      ),
      averageScore: overallCompetencyScore,
      attributesCount: beiAttributes.length,
    };
  }, [beiResults, assessmentData]);

  // Handle loading state
  if (loading) {
    return (
      <div className='w-full h-full flex items-center justify-center'>
        <div className='flex flex-col items-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500'></div>
          <span className='mt-4 text-gray-600'>
            Loading assessment summary...
          </span>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className='w-full h-full flex items-center justify-center'>
        <div className='bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md'>
          <div className='text-red-500 text-lg mb-2'>
            ❌ Error Loading Summary
          </div>
          <div className='text-red-700 mb-4'>{ error }</div>
          <button
              onClick={() => window.location.reload()}
              className='px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='h-full flex flex-col '>
      <div className='flex-1 overflow-y-auto'>
        <div className=' space-y-4'>
          { /* User Header Section */ }
          <div className='mb-4 bg-white backdrop-blur-sm rounded-2xl px-4 py-3 border border-gray-200/60 shadow-sm'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <NameEmailAvatar
                    name={candidateInfo.name}
                    email={candidateInfo.email}
                    avatar={''}
                />
                <div>
                  <p className='text-gray-600 text-xs font-medium'>
                    { candidateInfo.company }
                  </p>
                  <p className='text-[10px] text-gray-400'>
                    { candidateInfo.date }
                  </p>
                </div>
              </div>
            </div>
          </div>

          { /* Grid Layout for Charts */ }
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6'>
            { /* Technical Skills Section */ }
            { assessmentData?.skills && assessmentData.skills.length > 0 && (
              <div className='flex flex-col gap-4'>
                <PieCartWithStats
                    label='Technical Skills'
                    type='bar'
                    hideToggles={true}
                    maxValue={100}
                    legendWidth='w-[30%]'
                    barWidthOverride='20%'
                    data={{
                      Week: assessmentData.skills.map((skill: any) => ({
                        label: skill.skillName,
                        value:
                        skill.totalCount > 0
                          ? Math.round(
                            (skill.correctCount / skill.totalCount) * 100,
                          )
                          : 0,
                      })),
                    }}
                    colors={assessmentData.skills.map((skill: any) =>
                      getScoreHexColor(
                        skill.totalCount > 0
                          ? Math.round(
                            (skill.correctCount / skill.totalCount) * 100,
                          )
                          : 0,
                      ),
                    )}
                />

                { /* Technical Skills Summary Stats */ }
                <div className='grid grid-cols-3 gap-3'>
                  <div className='bg-blue-50/50 rounded-xl p-3 text-center border border-blue-100/30'>
                    <div className='text-lg font-bold text-blue-700 leading-none mb-1'>
                      { assessmentData.skills.reduce(
                        (sum: number, skill: any) => sum + skill.totalCount,
                        0,
                      ) }
                    </div>
                    <div className='text-[10px] font-semibold text-blue-600 uppercase tracking-wider'>
                      Questions
                    </div>
                  </div>
                  <div className='bg-emerald-50/50 rounded-xl p-3 text-center border border-emerald-100/30'>
                    <div className='text-lg font-bold text-emerald-700 leading-none mb-1'>
                      { assessmentData.skills.reduce(
                        (sum: number, skill: any) => sum + skill.correctCount,
                        0,
                      ) }
                    </div>
                    <div className='text-[10px] font-semibold text-emerald-600 uppercase tracking-wider'>
                      Correct
                    </div>
                  </div>
                  <div className='bg-gray-50/50 rounded-xl p-3 text-center border border-gray-100/30'>
                    <div className='text-lg font-bold text-gray-700 leading-none mb-1'>
                      { Math.round(
                        assessmentData.skills.reduce(
                          (sum: number, skill: any) =>
                            sum + skill.timeTakenForSkill / skill.totalCount,
                          0,
                        ) / assessmentData.skills.length,
                      ) }
                      s
                    </div>
                    <div className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                      Avg Time
                    </div>
                  </div>
                </div>
              </div>
            ) }

            { /* Behavioral Assessment Section */ }
            { summaryStats && (
              <div className='flex flex-col gap-4'>
                <PieCartWithStats
                    label='Behavioral Assessment'
                    type='bar'
                    hideToggles={true}
                    maxValue={100}
                    legendWidth='w-[30%]'
                    barWidthOverride='20%'
                    data={{
                      Week: summaryStats.beiAttributes.map((attr: any) => ({
                        label: attr.name,
                        value: attr.score,
                      })),
                    }}
                    colors={summaryStats.beiAttributes.map((attr: any) =>
                      getScoreHexColor(attr.score),
                    )}
                />

                { /* Behavioral Metric Cards */ }
                <div className='grid grid-cols-2 gap-3'>
                  <div className='group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-100/50 hover:border-blue-200/80 transition-all duration-200'>
                    <div className='text-lg font-bold text-blue-700 leading-none mb-1'>
                      { summaryStats.totalBehavioralExamples }
                    </div>
                    <div className='text-[10px] font-medium text-blue-600/80 uppercase tracking-wide'>
                      Responses provided
                    </div>
                  </div>

                  <div className='group bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-3 border border-emerald-100/50 hover:border-emerald-200/80 transition-all duration-200'>
                    <div className='text-lg font-bold text-emerald-700 leading-none mb-1'>
                      { Math.round(summaryStats.starFrameworkAdherence) }%
                    </div>
                    <div className='text-[10px] font-medium text-emerald-600/80 uppercase tracking-wide'>
                      STAR Adherence
                    </div>
                  </div>
                </div>
              </div>
            ) }
          </div>
        </div>
      </div>
    </div>
  );
};
